import { useState, useEffect, useRef, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'

function fmtTs(ts) {
  return new Date(ts).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  })
}

export default function ProjectNotesModal({
  projectId,
  projectReg,
  required = false,
  prompt = null,
  userProfile,
  canAddProjectNote = true,
  onClose,
}) {
  const { isTechnician } = useAuth()
  const [allNotes, setAllNotes]       = useState([])
  const [loading, setLoading]         = useState(true)
  const [newNote, setNewNote]         = useState('')
  const [saving, setSaving]           = useState(false)
  const [noteSubmitted, setNoteSubmitted] = useState(false)
  const [activeTab, setActiveTab]     = useState('project')
  const textareaRef = useRef(null)

  // Switch to milestone tab once auth resolves for technicians
  useEffect(() => { if (isTechnician) setActiveTab('milestone') }, [isTechnician])

  useEffect(() => { loadNotes() }, [projectId])

  // If modal is required, focus textarea once loaded
  useEffect(() => {
    if (!loading && required && textareaRef.current) textareaRef.current.focus()
  }, [loading, required])

  async function loadNotes() {
    setLoading(true)
    const { data } = await supabase
      .from('project_notes')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false })
    setAllNotes(data || [])
    setLoading(false)
  }

  // Split into project-level and subtask-level notes
  const projectNotes   = useMemo(() => allNotes.filter(n => !n.subtask_id), [allNotes])
  const milestoneNotes = useMemo(() => allNotes.filter(n =>  n.subtask_id), [allNotes])

  async function submitNote() {
    if (!newNote.trim()) return
    setSaving(true)
    const name = userProfile
      ? (userProfile.display_name || userProfile.email || null)
      : null
    const { data, error } = await supabase
      .from('project_notes')
      .insert({ project_id: projectId, body: newNote.trim(), created_by: userProfile?.id ?? null, created_by_name: name })
      .select()
      .single()
    if (!error && data) {
      setAllNotes(prev => [data, ...prev])
      setNewNote('')
      setNoteSubmitted(true)
    }
    setSaving(false)
  }

  async function deleteNote(id) {
    await supabase.from('project_notes').delete().eq('id', id)
    setAllNotes(prev => prev.filter(n => n.id !== id))
  }

  const canClose = !required || noteSubmitted

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Project Notes</h2>
            {projectReg && <p className="text-xs text-gray-400 mt-0.5">{projectReg}</p>}
          </div>
          <button
            onClick={() => canClose && onClose()}
            disabled={!canClose}
            className={`transition-colors ${canClose ? 'text-gray-400 hover:text-gray-600' : 'text-gray-200 cursor-not-allowed'}`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 px-6 flex-shrink-0">
          {[
            !isTechnician && { key: 'project',   label: 'Project Notes',   count: projectNotes.length },
            { key: 'milestone', label: 'Milestone Notes',  count: milestoneNotes.length },
          ].filter(Boolean).map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`relative py-2.5 px-1 mr-6 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === tab.key
                  ? 'text-gray-900 border-brand-red'
                  : 'text-gray-400 border-transparent hover:text-gray-600'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                  activeTab === tab.key ? 'bg-red-100 text-brand-red' : 'bg-gray-100 text-gray-500'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto">

          {/* ── Project Notes tab ────────────────────────────────────────────── */}
          {activeTab === 'project' && (
            <>
              {canAddProjectNote ? (
                <div className="px-6 pt-4 pb-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    {required && !noteSubmitted ? 'Required note' : 'Add a note'}
                  </label>
                  {prompt && !noteSubmitted && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-2">{prompt}</p>
                  )}
                  <textarea
                    ref={textareaRef}
                    rows={3}
                    value={newNote}
                    onChange={e => setNewNote(e.target.value)}
                    onKeyDown={e => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') submitNote() }}
                    placeholder="Detail project changes..."
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20 resize-none"
                  />
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-gray-400">Ctrl+Enter to submit</span>
                    <button
                      onClick={submitNote}
                      disabled={saving || !newNote.trim()}
                      className="text-sm font-medium bg-brand-red hover:bg-brand-red-dark disabled:opacity-50 text-white px-4 py-1.5 rounded-lg transition-colors"
                    >
                      {saving ? 'Saving…' : 'Submit'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="px-6 pt-4 pb-2">
                  <p className="text-xs text-gray-400 bg-gray-50 rounded-lg px-3 py-2">
                    Project notes are added by admins. To leave a note on a specific task, tap the task bar.
                  </p>
                </div>
              )}

              {!loading && projectNotes.length > 0 && (
                <>
                  <div className="border-t border-gray-100 mx-6" />
                  <div className="px-6 py-4 space-y-3">
                    {projectNotes.map(note => (
                      <div key={note.id} className="bg-gray-50 rounded-xl px-4 py-3 group">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-x-2 mb-1">
                              {note.created_by_name && (
                                <span className="text-xs font-semibold text-gray-700">{note.created_by_name}</span>
                              )}
                              <span className="text-xs text-gray-400">{fmtTs(note.created_at)}</span>
                            </div>
                            <p className="text-sm text-gray-800 whitespace-pre-wrap leading-snug">{note.body}</p>
                          </div>
                          {canAddProjectNote && (
                            <button
                              onClick={() => deleteNote(note.id)}
                              className="flex-shrink-0 p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all rounded"
                              title="Delete note"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
              {!loading && projectNotes.length === 0 && !required && (
                <p className="text-xs text-gray-400 text-center py-6">No project notes yet.</p>
              )}
            </>
          )}

          {/* ── Milestone Notes tab ──────────────────────────────────────────── */}
          {activeTab === 'milestone' && (
            <>
              {!loading && milestoneNotes.length > 0 ? (
                <div className="px-6 py-4 space-y-3">
                  {milestoneNotes.map(note => (
                    <div key={note.id} className="bg-gray-50 rounded-xl px-4 py-3">
                      <div className="flex flex-wrap items-center gap-x-2 mb-0.5">
                        {note.created_by_name && (
                          <span className="text-xs font-semibold text-gray-700">{note.created_by_name}</span>
                        )}
                        <span className="text-xs text-gray-400">{fmtTs(note.created_at)}</span>
                      </div>
                      {note.subtask_name && (
                        <p className="text-xs text-brand-red font-medium mb-1">{note.subtask_name}</p>
                      )}
                      <p className="text-sm text-gray-800 whitespace-pre-wrap leading-snug">{note.body}</p>
                    </div>
                  ))}
                </div>
              ) : (
                !loading && (
                  <div className="px-6 py-8 text-center">
                    <p className="text-xs text-gray-400">No milestone notes yet.</p>
                    <p className="text-xs text-gray-300 mt-1">Tap a task bar to add a note to a specific milestone.</p>
                  </div>
                )
              )}
            </>
          )}

        </div>

        {/* Footer close button — only when required and note has been submitted */}
        {required && noteSubmitted && (
          <div className="border-t border-gray-100 px-6 py-4 flex-shrink-0">
            <button
              onClick={onClose}
              className="w-full text-sm font-medium bg-brand-black text-white py-2 rounded-lg hover:opacity-90 transition-opacity"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
